import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUCKETS = [10, 25, 50, 75, 90, 100];

function sessionId() {
  let sid = sessionStorage.getItem('emina_sid');
  if (!sid) {
    sid = 's-' + Math.random().toString(36).slice(2, 10) + '-' + Date.now().toString(36);
    sessionStorage.setItem('emina_sid', sid);
  }
  return sid;
}

/**
 * Lightweight heatmap scroll collector.
 * Attach to a specific article container (or auto-detect on article route).
 * Sends milestone depth events + final dwell time on unmount/unload.
 */
export default function ScrollHeatmapCollector() {
  const loc = useLocation();
  const sentBuckets = useRef(new Set());
  const articleIdRef = useRef(null);
  const startedRef = useRef(Date.now());

  useEffect(() => {
    // Only active on article route
    const isArticle = /^\/clanak\//.test(loc.pathname);
    if (!isArticle) return;

    // Try to resolve article id from global or DOM data attribute
    const tryResolve = () => {
      const el = document.querySelector('[data-article-id]') || document.querySelector('[data-testid="article-page"]');
      const fromGlobal = window.__EMINA_ARTICLE_ID__;
      articleIdRef.current = fromGlobal || el?.getAttribute?.('data-article-id') || loc.pathname;
    };
    tryResolve();
    const resolveTimer = setTimeout(tryResolve, 1500);

    sentBuckets.current = new Set();
    startedRef.current = Date.now();

    const send = (depthPct, dwellSec) => {
      if (!articleIdRef.current) return;
      try {
        const body = JSON.stringify({
          article_id: articleIdRef.current,
          depth_pct: depthPct,
          dwell_seconds: dwellSec,
          session_id: sessionId(),
        });
        if (navigator.sendBeacon) {
          navigator.sendBeacon(`${API}/public/heatmap/event`, new Blob([body], { type: 'application/json' }));
        } else {
          fetch(`${API}/public/heatmap/event`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true,
          }).catch(() => {});
        }
      } catch { /* silent */ }
    };

    const onScroll = () => {
      const doc = document.documentElement;
      const max = Math.max(1, doc.scrollHeight - doc.clientHeight);
      const pct = Math.min(100, Math.round((doc.scrollTop || window.scrollY || 0) / max * 100));
      for (const b of BUCKETS) {
        if (pct >= b && !sentBuckets.current.has(b)) {
          sentBuckets.current.add(b);
          const dwell = (Date.now() - startedRef.current) / 1000;
          send(b, dwell);
        }
      }
    };
    const onUnload = () => {
      if (!articleIdRef.current) return;
      const dwell = (Date.now() - startedRef.current) / 1000;
      // Send final snapshot
      const doc = document.documentElement;
      const max = Math.max(1, doc.scrollHeight - doc.clientHeight);
      const pct = Math.min(100, Math.round((doc.scrollTop || window.scrollY || 0) / max * 100));
      send(pct, dwell);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('beforeunload', onUnload);
    window.addEventListener('pagehide', onUnload);
    // Initial measure
    setTimeout(onScroll, 500);

    return () => {
      clearTimeout(resolveTimer);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('beforeunload', onUnload);
      window.removeEventListener('pagehide', onUnload);
      onUnload();
    };
  }, [loc.pathname]);

  return null;
}
